import { h, render, Component, createRef } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';

import localUtil from '../../lib/util';
import Input from '../../components/form/input';
import { getImageFilesFromClipboard, uploadImageFile, imageMarkdown, insertTextAtCursor } from '../../lib/pasteImageUpload';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

const MARKDOWN_OPTIONS = {
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
    },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

function isValidDate(dateString) {
    // console.log('isValidDate', dateString);

    const date = new Date(dateString);
    // Check if the date is invalid
    if (isNaN(date)) {
        // console.log('isNaN(date)', isNaN(date));
        return false;
    }
    // Check if the year is before 1800
    if (date.getFullYear() < 0) {
        // console.log('date.getFullYear() < 0', date.getFullYear() < 0);
        return false;
    }
    // If we reach here, the date is valid
    return true;
}

@observer
class InputSectionToggle extends Component {

    render() {
        const { section, toggleSectionValues, toggleSectionFunction } = this.props;
        const key = section?.key;
        const safeKey = key.replace(/ /g, '_');
        return (<>
            <div class='position-absolute d-flex flex-column align-items-center' style='right: -70px; top: 0px;'>
                <div class='form-check custom-switch custom-switch-lg d-flex justify-content-end'>
                    <input
                        type='checkbox'
                        class='inputForm form-check-input'
                        id={`${safeKey}InputToggle`}
                        name={key}
                        value={(toggleSectionValues[key] === 1 || toggleSectionValues[key] === true || toggleSectionValues[key] === '1' || toggleSectionValues[key] === 'true') ? 0 : 1}
                        onInput={toggleSectionFunction}
                        checked={toggleSectionValues[key] == 1 ? 'checked' : ''}
                        autocomplete={'off'}
                    />
                    <label class='form-check-label ps-2' for={`${safeKey}InputToggle`} />
                </div>
                <small class='text-muted'>Hidden</small>
            </div>
        </>);
    }
}

@withText(props => ({
    searchTemplatePlaceholder: <Text id='texttemplate.search-placeholder'>Search for templates...</Text>,
}))
@observer
class FormFields extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            showSection: '',
            updateInProgress: {},
            textTemplateSaved: {},
            isInfocus: {},
            textTemplates: [],
            sectionCollapsed: {},
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
            templateSearchQuery: '',
            selectedTemplatePreview: null,
            pasteImageUploading: false,
        };
        this.updateTimer = {};
        this.sectionRefs = {};
    }

    // Paste a screenshot/image directly into a textarea field.
    // Uploads the image and inserts a markdown image link at the cursor. The inserted
    // text is dispatched as an 'input' event so the normal autosave path persists it.
    // Non-image pastes (plain text) fall through untouched.
    handlePasteCapture = async (e) => {
        const target = e.target;
        if (!target || target.tagName !== 'TEXTAREA') {
            return;
        }
        const files = getImageFilesFromClipboard(e);
        if (files.length === 0) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.setState({ pasteImageUploading: true });
        try {
            // Upload sequentially so multiple pasted images are inserted in order.
            for (let i = 0; i < files.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                const uploadedFile = await uploadImageFile(files[i]);
                insertTextAtCursor(target, imageMarkdown(uploadedFile));
            }
        } catch (err) {
            console.error('Paste image upload failed:', err);
        } finally {
            this.setState({ pasteImageUploading: false });
        }
    }

    setUpdateInProgress = (field, value) => {
        const { updateInProgress } = this.state;
        this.setState({
            updateInProgress: {
                ...updateInProgress,
                [field]: value,
            },
        });
    }

    adminUpdate = (e, callback, checkValue) => {
        const { id } = e.target.closest('.inputForm').dataset;
        const { datatype } = e.target.dataset;
        const idAsInt = parseInt(id, 10);
        const { name, value: rawValue, type } = e.target;
        // console.log('adminUpdate', { id, name, value: rawValue, type, datatype });
        this.setUpdateInProgress(`${id}-${name}`, true);

        let value = rawValue;
        if (type === 'number') {
            value = value.replace(',', '.');
            value = value.replace(/[^0-9.]/g, '');
        }
        if (datatype === 'decimal') {
            value = parseFloat(value);
        }
        if (datatype === 'integer') {
            value = parseInt(value, 10);
        }

        const field = `${idAsInt}-${name}`;
        // console.log('adminUpdate', { id, name, field, value, type, datatype });
        const {
            holdingStore,
            isAdmin,
            objectName,
            // callback = () => {},
            onInputcallback = () => {},
        } = this.props;
        if (type === 'datetime-local' && !isValidDate(value)) {
            // console.log('adminUpdate invalid date', { id, name, field, value, type })
            return;
        }
        if (type === 'date' && !isValidDate(value)) {
            // console.log('adminUpdate invalid date', { id, name, field, value, type })
            return;
        }

        // Update local values
        holdingStore.adminUpdateLocal(idAsInt, { [name]: value }, objectName);
        // holdingStore.adminUpdateLocal(idAsInt, { [name]: value }, 'adminList');
        holdingStore.updateObjectKeyValue(objectName, name, value);
        // Callback for input updates
        onInputcallback(id, name, value);

        clearTimeout(this.updateTimer[field]);
        this.updateTimer[field] = setTimeout(() => {
            this.doAdminUpdate({ id, idAsInt, name, value, callback, checkValue });
        }, 700);
    }

    updateValue = async ({ name, value, id }) => {
        const { holdingStore, objectName } = this.props;
        // console.log('updateValue', { objectName, name, value });
        holdingStore.updateObjectKeyValue(objectName, name, value);
        if (id) {
            const idAsInt = parseInt(id, 10);
            await holdingStore.saveField(idAsInt, name, value);
        }
    }

    doAdminUpdate = async ({ id, idAsInt, name, value, callback = () => {}, checkValue = () => {} }) => {
        // const { id } = e.target.closest('.inputForm').dataset;
        // const idAsInt = parseInt(id, 10);
        // const { name, value } = e.target;
        const {
            holdingStore,
            isAdmin,
            objectName,
            // checkValue = () => {},
            // callback = () => {},
        } = this.props;
        // console.log('doAdminUpdate', { id, name, value });
        if (holdingStore) {
            if (isAdmin) {
                await holdingStore.adminUpdate(idAsInt, { [name]: value }, objectName);
            } else {
                // console.log('doAdminUpdate !isAdmin', { id: idAsInt, name, value });
                await holdingStore.saveField(idAsInt, name, value);
            }
            this.setUpdateInProgress(`${id}-${name}`, false);
    // console.log('doAdminUpdate', { id, name, value });

            // object, this.updateValue, holdingStore

            callback(value);
            checkValue(value);
        }
    }

    displayValue = (field, val) => {
        const { holdingStore } = this.props;
        // if (field.name === 'members' && holdingStore[field.lookupArray]) {
        //     const member = holdingStore[field.lookupArray].find((m) => m.id === parseInt(val, 10));
        //     if (member) {
        //         return localUtil.displayName(member, true);
        //     }
        // }
        return val;
    };

    displayValueArrayObject = (field, val) => {
        if (field.displayArrayObject) {
            return field.displayArrayObject(val);
        }
        if (field.name === 'youtubeVideos') {
            return (<img src={localUtil.youtubeThumb(val.url)} style='max-height: 115px;' />);
        }
        if (field.name === 'parts') {
            return `${val.qty} x ${val.partNumber} ${val.name}`;
        }
        if (field.name === 'members') {
            return `${val.firstname} ${val.lastname} ${val.email}`;
        }
        if (field.name === 'tags') {
            return `${val.name}`;
        }
        if (field.name === 'inspections') {
            const values = [];
            for (let i = 1; i <= 5; i += 1) {
                if (val[`check${i}`]) {
                    values.push(val[`check${i}`]);
                }
            }
            if (val[`name`]) {
                values.push(val[`name`]);
            }
            return (<>
                <div class='d-flex w-100 flex-column'>
                    <div class='w-100'>
                        {val.images && <img src={val.images[0].s3MediumLink} class='img-fluid' style='max-height: 100px;' />}
                    </div>
                    {values.map((v, idx) => {
                        return (<>
                            <div class='w-100 d-flex flex-row text-start'>
                                <div class='mx-2'>
                                    <i class='fa-solid fa-check' />
                                </div>
                                <div class='flex-fill'>
                                    {v}
                                </div>
                            </div>
                        </>);
                    })}
                </div>
            </>);
        }
        return val?.title;
    }

    renderField(field, line) {
        const apiServer = util.getApiServer();

        if (field.showQR) {
            const value = field.getQrValue ? field.getQrValue(line) : `${apiServer}/go/${field.getValue(line)}`;
            return (<>
                <QRCodeSVG value={value} size={400} />
            </>);
        }
        if (field.showBarcode) {
            const value = field.getBarcodeValue ? field.getBarcodeValue(line) : field.getValue(line);
            if (!value) {
                return (<></>);
            }
            return (<>
                <Barcode value={value} />
                <button class='btn btn-link' data-value={value} data-name={field.name} onClick={this.onClickPrintBarcode}>
                    <i class='fa-solid fa-print' />
                </button>
            </>);
        }

        if (field.showImage) {
            return (<>
                {field.getValue(line).map((image, idx) => {
                    return (<>
                        <img src={`${image.s3MediumLink}`} style='max-width: 100px;' />
                    </>);
                })}
            </>);
        }
        if (field.showFile) {
            return (<>
                {field.getValue(line).map((file, idx) => {
                    return (<>
                        <a
                            href={file.s3Link}
                            title={file.name}
                            target='_blank'
                            native
                            style='font-size: 2.0em;'
                        ><i class='fa-solid fa-file-pdf' /></a>
                    </>);
                })}
            </>);
        }
        if (field.name === 'youtubeVideos') {
            if (!field.getValue(line) || !field.getValue(line).length) {
                return (<></>);
            }
            return (<>
                {field.getValue(line).map((video, idx) => {
                    return (<>
                        <a
                            href={`https://www.youtube.com/watch?v=${video.url}`}
                            title={video.title}
                            target='_blank'
                            native
                            style='font-size: 2.0em;'
                        >
                            <img src={localUtil.youtubeThumb(video.url)} style='max-height: 80px;' />
                        </a>
                    </>);
                })}
            </>);
        }

        return (<>
            <span class='form-label-inline text-muted'>{field.placeholder}</span>
            <div class={`pt-4 pb-2 py-2 ps-2 ${field.type === 'number' ? 'text-center' : ''}`}>
                {field.getValue(line)}
            </div>
        </>);
    }

    chooseItem = async (props) => {
        const {
            holdingStore,
            objectName,
            object: inputObject,
        } = this.props;
        const { id, item, listName, replaceKeys } = props;
        const object = inputObject || holdingStore[objectName];

        // console.log({ id, item, listName, replaceKeys });
        const updateObject = {};
        for (let i = 0, l = replaceKeys.length; i < l; i += 1) {
            const keyPair = replaceKeys[i];
            const [fromKey, toKey] = keyPair;
            const value = item[fromKey];
             // Update local values
            //  holdingStore.adminUpdateLocal(id, { [toKey]: value }, objectName);
            holdingStore.updateObjectKeyValue(objectName, toKey, value);
            updateObject[toKey] = value;
        }

        if (object?.id) {
            await holdingStore.save(updateObject, id);
        }
        holdingStore.updateKeyValue(listName, []);
    }

    setSection = (e) => {
        const { showSection } = this.state;
        const { section } = e.target.closest('span.badge').dataset;
        this.setState({
            showSection: showSection === section ? '' : section,
        });
    }

    onFocus = (e, field, object, holdingStore, objectName) => {
        const { name } = e.target;
        const { isInfocus } = this.state;
        isInfocus[name] = true;
        this.setState({ isInfocus });

        // Call focusCallback if field has one and value is empty
        if (field && field.focusCallback && !field.getValue(object)) {
            field.focusCallback(object, holdingStore, objectName);
        }
    }

    onBlur = (e) => {
        const { name } = e.target;
        const { isInfocus } = this.state;
        setTimeout(() => {
            delete isInfocus[name];
            this.setState({ isInfocus });
        }, 2000);
    }

    handleSelectTextTemplate = async (e) => {
        const { textTemplates } = this.state;
        const { textTemplateStore } = this.props.stores;
        const { holdingStore, objectName, object, objectUpdate } = this.props;
        // const { textTemplates } = textTemplateStore;
        const { section, field } = e.target.closest('select').dataset;
        const { value } = e.target;
        const textObject = textTemplates.find(t => t.id === parseInt(value, 10));
        const currentObject = object || holdingStore[objectName];
        const currentValue = currentObject[field] || '';
        // console.log('handleSelectTextTemplate', { objectName, section, field, value, textObject, currentObject, currentValue });
        // console.log('objectUpdate', objectUpdate);
        if (objectUpdate) {
            objectUpdate({
                id: currentObject.id,
                name: field,
                value: `${currentValue}${currentValue ? '\n\n' : ''}${textObject.body}`,
                md5: currentObject.md5,
            })
        } else {
            holdingStore.updateObjectKeyValue(objectName, field, `${currentValue}${currentValue ? '\n\n' : ''}${textObject.body}`);
            const updateObject = {};
            updateObject[field] = textObject.body;
            if (currentObject?.id) {
                await holdingStore.save(updateObject, currentObject.id);
            }
        }
    }

    openTemplateModal = (section, field) => {
        this.setState({
            showTemplateModal: true,
            templateModalSection: section,
            templateModalField: field,
            templateSearchQuery: '',
            selectedTemplatePreview: null,
        });
    }

    closeTemplateModal = () => {
        this.setState({
            showTemplateModal: false,
            templateModalSection: '',
            templateModalField: '',
            templateSearchQuery: '',
            selectedTemplatePreview: null,
        });
    }

    handleTemplateSearch = (e) => {
        this.setState({ templateSearchQuery: e.target.value });
    }

    selectTemplateFromModal = (template) => {
        this.setState({ selectedTemplatePreview: template });
    }

    applySelectedTemplate = async () => {
        const { selectedTemplatePreview, templateModalSection, templateModalField } = this.state;
        if (!selectedTemplatePreview) return;

        const { holdingStore, objectName, object, objectUpdate } = this.props;
        const currentObject = object || holdingStore[objectName];
        const currentValue = currentObject[templateModalField] || '';

        if (objectUpdate) {
            objectUpdate({
                id: currentObject.id,
                name: templateModalField,
                value: `${currentValue}${currentValue ? '\n\n' : ''}${selectedTemplatePreview.body}`,
                md5: currentObject.md5,
            });
        } else {
            holdingStore.updateObjectKeyValue(objectName, templateModalField, `${currentValue}${currentValue ? '\n\n' : ''}${selectedTemplatePreview.body}`);
            const updateObject = {};
            updateObject[templateModalField] = selectedTemplatePreview.body;
            if (currentObject?.id) {
                await holdingStore.save(updateObject, currentObject.id);
            }
        }

        this.closeTemplateModal();
    }

    saveTextTemplate = async (e) => {
        const dialogAction = beginDialogAction(this, 'saveTextTemplate', () => [
            (this.props.object || this.props.holdingStore?.[this.props.objectName])?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { textTemplateStore } = this.props.stores;
            const { holdingStore, objectName, object } = this.props;
            const { section, field } = e.target.closest('button').dataset;
            const currentObject = object || holdingStore[objectName];
            const templateBody = currentObject[field];

            if (!((await appConfirm('Are you sure you want to save this text as a template for everyone?')) && dialogAction.isCurrent())) {
                return;
            }

            const response = await textTemplateStore.insert({
                section,
                field,
                body: templateBody,
            });
            if (response.status === 201) {
                const { textTemplateSaved } = this.state;
                textTemplateSaved[`${field}`] = true;
                this.setState({ textTemplateSaved });
                setTimeout(() => {
                    const { textTemplateSaved } = this.state;
                    delete textTemplateSaved[`${field}`];
                    this.setState({ textTemplateSaved });
                }, 3000);
            }
        } finally {
            dialogAction.finish();
        }
    }

    toggleSection = (e) => {
        const { sectionCollapsed } = this.state;
        const { key } = e.target.closest('.section-button').dataset;
        sectionCollapsed[key] = !sectionCollapsed[key];
        this.setState({ sectionCollapsed });

        if (this.sectionRefs[key]) {
            this.sectionRefs[key].scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }

    toggleAdminFields = () => {
        const { appState } = this.props.stores;
        const { showAdminFields } = appState;
        appState.setShowAdminFields(!showAdminFields);
    }

    loadAll = async () => {
        const {
            sections,
            holdingStore,
            sectionsNotCollapsed,
            object: inputObject,
            objectName,
        } = this.props;

        const object = inputObject || holdingStore[objectName];

        holdingStore.updateKeyValue('feedback', {});
        holdingStore.updateKeyValue('feedbackButtonFunc', {});
        holdingStore.updateKeyValue('feedbackButtonTitle', {});
        holdingStore.updateKeyValue('feedbackButtonProgress', {});
        holdingStore.updateKeyValue('feedbackButtonDone', {});
        holdingStore.updateKeyValue('feedbackButtonInfo', {});

        const sectionKeys = sections.map(s => s.key);
        if (!sectionsNotCollapsed) {
            const sectionCollapsed = {};
            for (let i = 0, l = sections.length; i < l; i += 1) {
                const section = sections[i];
                if (section.isCollapsed) {
                    sectionCollapsed[section.key] = true;
                }
                for (let j = 0, ll = section.fields.length; j < ll; j += 1) {
                    const field = section.fields[j];
                    if (field.before) {
                        field.before(object, holdingStore);
                    }
                }
            }
            this.setState({ sectionCollapsed });
        }

        // console.log('loadAll', { sections, sectionKeys });
        const { textTemplateStore } = this.props.stores;
        const tpls = await textTemplateStore.load({ query: { section: sectionKeys, limit: 500 }, skipUpdate: 1 });

        this.setState({ textTemplates: tpls });
    }

    onClickPrintBarcode = e => {
        e.preventDefault();
        e.stopPropagation();
        const { value, name } = e.target.closest('button').dataset;
        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <Barcode value={value} /><br />
        </IntlProvider>;

        // Append to document
        const printDiv = document.createElement('div');
        printDiv.className = 'receipt';
        document.body.appendChild(printDiv);
        render(receipt, printDiv);
        // Trigger print dialog
        window.print();
        // Remove the appended div
        document.body.removeChild(printDiv);
        // Re-render the receipt component to reset it
        render(null, printDiv);
    }

    viewTags = obj => {
        if (obj.tags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0) {
            return '';
        }
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tagClass = darkmode ? 'dark' : 'light';

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.files?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        return (<>
                            <span
                                class={`badge text-bg-${tagClass} me-1 mt-1`}
                                style='font-size: 0.7em; cursor: pointer;'
                                onClick={this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</span>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { userStore, textTemplateStore, appState } = this.props.stores;
        const { showAdminFields } = appState;
        const {
            showSection,
            updateInProgress = {},
            isInfocus,
            textTemplateSaved,
            textTemplates = [],
            sectionCollapsed = {},

            templateModalSection,
            templateModalField,
            templateSearchQuery,
            selectedTemplatePreview,
            showTemplateModal,
            pasteImageUploading,
        } = this.state;
        const {
            sections,
            fields,
            holdingStore,
            objectName,
            object: inputObject,
            isNew,
            drawerLevel,
            lookupArray,
            rightHeader,
            rightHeaderClick = () => {},
            hideAdmin = !showAdminFields,
            filter = fields ? f => fields.includes(f.name) : () => true,
            onInput,
            marginTop = 'mt-3',
            headerMargin = 'mx-3',
            sectionMargin = 'mx-3',
            skipSurroundingDivs = false,
            onKeyDown = () => {},
            isAdmin: isAdminProp,
            fieldsOpts,
            hideFields,
            hideSections,
            hideFilters,
            showRequiredFieldsInfo,
            showSectionName = true,
            sectionsNotCollapsed,
            toggleFieldFunction,
            toggleFieldValues = {},
            toggleSectionFunction,
            toggleSectionValues = {},
            openFieldSettings,
            onInputcallback = () => {},
            callback = () => {},
            drawerCallback = () => {},
            classNames,
            readOnly,
            searchTemplatePlaceholder,
        } = this.props;
        // const { textTemplates } = textTemplateStore;
        const { user, isAdmin, isCustomerAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const object = inputObject || holdingStore[objectName] || {};
        // console.log('object', object);
        // Return loading spinner if editing and object is not loaded yet
        if (!isNew && (!object || Object.keys(object).length === 0)) {
            return (<div class='p-3 text-center text-muted'><i class='fa-duotone fa-spinner fa-spin' /></div>);
        }
        const finalSections = fields ? sections.filter(s => {
            return s.fields.some(f => fields.includes(f.name));
        }) : sections;
        // console.log('finalSections', finalSections);

        // return (<>
        //     <xmp>finalSections: {JSON.stringify(finalSections, null, 2)}</xmp>
        // </>);
        return (<>
            {pasteImageUploading && (
                <div
                    class='position-fixed start-50 translate-middle-x bg-dark text-white px-3 py-2 rounded shadow d-flex align-items-center'
                    style='bottom: 20px; z-index: 1080;'
                >
                    <i class='fa-duotone fa-spinner fa-spin me-2' />
                    <Text id='imageupload.uploading-image'>Uploading image...</Text>
                </div>
            )}
            {!skipSurroundingDivs && finalSections && finalSections.length > 1 && <>
                {hideFilters ? <>
                </> : <>
                    <div class={`${headerMargin} font-weight-lighter px-3 py-1 text-muted`}>
                        <small class='text-uppercase'>
                            <Text id='form.filter-sections'>Filter</Text>
                        </small>
                    </div>
                    <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${sectionMargin} d-flex flex-wrap py-3 px-3 box-container`}>
                        {isAdmin && <>
                            <span
                                class={`badge text-bg-${showAdminFields ? 'danger' : 'light'} font-weight-bold me-2 mt-2`}
                                onClick={this.toggleAdminFields}
                            >Show Admin</span>
                        </>}
                        {finalSections && finalSections.filter(s => {
                            if (hideSections && hideSections.length > 0) {
                                return !hideSections.some(e => e.key === s.key);
                            }
                            // Hide admin-only sections from non-admins or when admin fields are hidden
                            if (s.isAdminSection && (!isAdmin || hideAdmin)) {
                                return false;
                            }
                            return true;
                        }).map((section, idx) => {
                            return (<>
                                <span
                                    data-section={section.key}
                                    class={`badge text-bg-${section.key === showSection ? 'primary' : 'light'} font-weight-normal me-2 mt-2`}
                                    onClick={this.setSection}
                                >
                                    {section.name}
                                    <span class={`ms-1 text-${section.key === showSection ? 'white' : 'muted'}`}>
                                        ({section.fields.filter(filter).filter(f => f.getValue(object)).length}/{section.fields.filter(filter).length})
                                    </span>
                                    {section.key === showSection ? <i class='fa-solid fa-times ms-1' /> : ''}
                                </span>
                            </>);
                        })}
                    </div>
                </>}
            </>}

            {finalSections && finalSections.filter(s => {
                if (hideSections && hideSections.length > 0) {
                    return !hideSections.some(e => e.key === s.key);
                }
                // Hide admin-only sections from non-admins or when admin fields are hidden
                if (s.isAdminSection && (!isAdmin || hideAdmin)) {
                    return false;
                }
                if (showSection && showSection !== '') {
                    return s.key === showSection;
                }
                return true;
            }).map((section, idx) => {
                let sectionFieldIdx = 0;
                const fields = section.fields.filter(f => {
                    if (hideFields && hideFields.length > 0) {
                        return !hideFields.some(e => e.name === f.name);
                    }
                    return true;
                });
                const hasAllFields = section.hasAllRequiredFields ? section.hasAllRequiredFields(object, fields) : true;

                const finalShowSectionName = util.isDefined(section.showSectionName) ? section.showSectionName : showSectionName;
                // console.log('section', { finalShowSectionName, showSectionName });

                return (<>
                    {section.foundListName && holdingStore[section.foundListName] && holdingStore[section.foundListName].length > 0 && <>
                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                                <div class='mx-4 font-weight-lighter px-3 py-1 text-muted' >
                                    <small class='text-uppercase'>
                                        {section.foundListText ? section.foundListText : <Text id='input.found-similar'>Found similar...</Text>}
                                    </small>
                                </div>
                                <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 d-flex flex-column px-0 overflow-auto rounded-lg`} style='max-height: 35vh;'>
                                    {holdingStore[section.foundListName].map((item, idx) => {
                                        const keys = section.foundListDisplayKeys;
                                        return(<>
                                            <div class='d-flex flex-row justify-content-between align-items-center'>
                                                <div
                                                    class='flex-fill similar-item py-2 px-3'
                                                    onClick={() => this.chooseItem({
                                                        item,
                                                        listName: section.foundListName,
                                                        replaceKeys: section.foundListReplaceKeys,
                                                    })}
                                                >
                                                    {keys && keys.map(key => <>
                                                        <span class='me-2'>{item[key]}</span>
                                                    </>)}
                                                </div>
                                                {/* {item.images[0] && <>
                                                    <div>
                                                        <div class=''>
                                                            <img src={`${item.images[0].s3MediumLink}`} class='rounded-lg img-fluid' style='max-height: 45px;' />
                                                        </div>
                                                    </div>
                                                </>} */}
                                            </div>
                                        </>);
                                    })}
                                </div>
                            </div>
                        </div>
                    </>}

                    {(!skipSurroundingDivs && (showRequiredFieldsInfo || finalShowSectionName || rightHeader || toggleSectionFunction))&& <div
                        class={`${headerMargin} ${section.marginTop || marginTop} font-weight-lighter px-3 py-1 text-muted position-relative`}
                        ref={c => this.sectionRefs[section.key] = c}
                    >
                        <small class='text-uppercase section-button' onClick={this.toggleSection} data-key={section.key}>
                            {showRequiredFieldsInfo && <>
                                {hasAllFields ? <>
                                    <i class='fa-duotone fa-circle-check text-success me-2'></i>
                                </> : <>
                                    <i class='fa-duotone fa-circle-xmark text-danger me-2'></i>
                                </>}
                            </>}
                            {finalShowSectionName && <>
                                {section.name}
                                {sectionCollapsed[section.key] ? <><i class='fa-duotone fa-chevron-up ms-2' /></> : <><i class='fa-duotone fa-chevron-down ms-2' /></>}
                            </>}
                            {rightHeader && <>
                                <div class='float-right' onClick={rightHeaderClick}>
                                    {rightHeader}
                                </div>
                            </>}
                        </small>
                        {toggleSectionFunction && <InputSectionToggle
                            section={section}
                            toggleSectionValues={toggleSectionValues}
                            toggleSectionFunction={toggleSectionFunction}
                        />}
                    </div>}

                    {sectionCollapsed[section.key] ? <>
                        <div
                            class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${sectionMargin} d-flex flex-row justify-content-center align-items-center box-container section-button px-3 py-3 text-muted`}
                            onClick={this.toggleSection}
                            data-key={section.key}
                        >
                            {section.displayCollapsedInfo ? section.displayCollapsedInfo(object, hideFields) : <>
                                <i class='fa-solid fa-chevron-down me-2' /> <Text id='input.click-to-view-section'>Click to view section</Text>: {section.name}
                            </>}
                        </div>
                    </> : <>
                        <div
                            class={!skipSurroundingDivs ? `${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${sectionMargin} d-flex flex-column box-container` : 'w-100'}
                            onPasteCapture={this.handlePasteCapture}
                            style={`
                                ${section.backgroundColor ? `background-color: ${section.backgroundColor};` : ''}
                                ${section.borderColor ? `border: 1px solid ${section.borderColor};` : ''}
                            `}
                        >
                            {section.fields && section.fields.filter(filter).map((field, idx) => {
                                if (hideFields && hideFields.some(e => e.name === field.name)) {
                                    return (<></>);
                                }
                                if (isAdmin && field.hideIfAdmin) {
                                    return (<></>);
                                }
                                let isReadOnly = isAdmin ? (field.isReadOnly && !field.adminEditAllowed) : field.isReadOnly;
                                if (field.noEdit && !isNew) {
                                    isReadOnly = isAdmin ? !field.adminEditAllowed : true;
                                }
                                if (readOnly) {
                                    isReadOnly = true;
                                }
                                if ((field.isAdminField && hideAdmin) || (field.isAdminField && !isAdmin)) {
                                    return (<></>);
                                }
                                if (isNew && field.hideOnCreate) {
                                    return (<></>);
                                }
                                if (isNew && field.defaultValue && !field.getValue(object)) {
                                    holdingStore.updateObjectKeyValue(objectName, field.name, field.defaultValue);
                                }
                                const textTpls = textTemplates.filter(t => t.section === section.key && t.field === field.name);
                                const line = field.getValue(object);

                                let datalist = field.datalist;
                                if (field.datalistName && Array.isArray(holdingStore[field.datalistName])) {
                                    datalist = holdingStore[field.datalistName].map(item => {
                                        return [
                                            field.datalistFieldKey(item),
                                            field.datalistFieldVal(item),
                                        ];
                                    });
                                    if (field.addToDatalist) {
                                        datalist.push(field.addToDatalist);
                                    }
                                }
                                sectionFieldIdx += 1;

                                if (field.conditionFunc) {
                                    if (!field.conditionFunc(object)) {
                                        return (<></>);
                                    }
                                }

                                if (fieldsOpts && fieldsOpts[`${section.key}.${field.name}`]) {
                                    field = {
                                        ...field,
                                        ...fieldsOpts[`${section.key}.${field.name}`],
                                    };
                                }

                                return (<>
                                    <div
                                        class={`d-flex flex-row align-items-center position-relative ps-2 pe-2 w-100`}
                                        style={`
                                            ${sectionFieldIdx > 1 ? `border-top: 1px ${darkmode ? '#303030' : '#f0f0f0'} solid;` : ''}
                                        `}
                                    >
                                        {['image', 'file', 'arrayObject'].includes(field.type) && object[field.name]?.length > 2 ? <></> : <>
                                            {(!skipSurroundingDivs || field.type === 'image') && <div
                                                class={`d-flex justify-content-center align-items-center ${field.isAdminField && isAdmin ? 'text-danger' : 'text-muted '}`}
                                                style={`
                                                width: 44px;
                                                height: 44px;
                                                min-width: 44px;
                                                opacity: ${field.isAdminField && isAdmin ? '0.8' : '0.3'};
                                                `}
                                            >
                                                <i class={`${field.icon}`} />
                                            </div>}
                                        </>}
                                        <div class={`flex-grow-1 position-relative`}>
                                            {isReadOnly ? <>
                                                {line ? this.renderField(field, object) : <>
                                                    <span class='form-label-inline text-muted'>{field.placeholder}</span>
                                                    <div class='pt-4 pb-2 ps-2'>
                                                        <span class={`text-muted font-weight-lighter`}>
                                                            <Text id='form.field-empty'>No value for</Text> {field.name}
                                                        </span>
                                                    </div>
                                                </>}
                                            </> : <>
                                                <Localizer>
                                                    <Input
                                                        key={`${object.id || 'isNew'}-${field.name}-${section.key}`.replace(/[^a-zA-Z0-9-]/g, '-')}
                                                        stores={this.props.stores}
                                                        isAdmin={isAdminProp}
                                                        drawerLevel={drawerLevel}
                                                        holdingStore={holdingStore}
                                                        objectName={objectName}
                                                        object={object}
                                                        type={field.type}
                                                        field={field.name}
                                                        aiJournal={field.aiJournal}
                                                        aiPost={field.aiPost}
                                                        audioTranscribe={field.audioTranscribe}
                                                        section={section.key}
                                                        fieldObject={field}
                                                        step={field.step}
                                                        viewHtml={field.viewHtml}
                                                        min={field.minFunc ? field.minFunc(object) : field.min}
                                                        max={field.maxFunc ? field.maxFunc(object) : field.max}
                                                        value={line}
                                                        displayArrayValue={(val) => this.displayValue(field, val)}
                                                        displayArrayObject={(val) => this.displayValueArrayObject(field, val)}
                                                        datalist={datalist}
                                                        datalistHelp={field.datalistHelp}
                                                        datalistValues={field.datalistValues}
                                                        useFirstValueAsBackgroundColorForOption={field.useFirstValueAsBackgroundColorForOption}
                                                        title={field.title}
                                                        placeholder={field.placeholder}
                                                        help={field.help}
                                                        showDanger={field.showDanger}
                                                        showWarning={field.showWarning}
                                                        showFieldInfo={field.showFieldInfo}
                                                        maxLength={field.maxLength}
                                                        onInput={onInput || (isNew ? undefined: this.adminUpdate)}
                                                        callback={(value) => field.callback ? field.callback(object, this.updateValue, holdingStore, value) : null}
                                                        drawerCallback={() => field.drawerCallback ? field.drawerCallback(object, this.updateValue, holdingStore) : drawerCallback(object, this.updateValue, holdingStore)}
                                                        loadCallback={() => field.loadCallback ? field.loadCallback(object, this.updateValue, holdingStore) : null}
                                                        checkValue={(value) => field.checkValue ? field.checkValue(object, holdingStore, value) : null}
                                                        before={() => field.before ? field.before(object, holdingStore) : null}
                                                        onInputcallback={onInputcallback}
                                                        // onInputcallbackField={field.onInputcallbackField}
                                                        extraDataset={{
                                                            'data-isnew': isNew,
                                                            'data-id': object.id,
                                                            'data-md5': object.md5,
                                                        }}
                                                        drawerSize={field.drawerSize}
                                                        drawerName={field.drawerName}
                                                        drawerRight={field.drawerRight}
                                                        width={field.displayWidth}
                                                        height={field.displayHeight}
                                                        lookupArray={field.lookupArray || section.lookupArray || lookupArray}
                                                        foundListName={field.foundListName || section.foundListName}
                                                        foundListKeysName={field.foundListKeysName || section.foundListKeysName}
                                                        updateInProgress={updateInProgress[`${object.id}-${field.name}`]}
                                                        onFocus={(e) => this.onFocus(e, field, object, holdingStore, objectName)}
                                                        onBlur={this.onBlur}
                                                        onKeyDown={onKeyDown}
                                                        skipSurroundingDivs={skipSurroundingDivs}
                                                        addToImages={field.addToImages}
                                                        usefulValues={field.usefulValues}
                                                        toggleFieldFunction={toggleFieldFunction}
                                                        toggleFieldValues={toggleFieldValues}
                                                        openFieldSettings={openFieldSettings}
                                                        isRequired={field.isRequired}
                                                        isRecommended={field.isRecommended}
                                                        classNames={classNames}
                                                        addFileText={field.addFileText}
                                                        displayLargeImages={field.displayLargeImages}
                                                        displayLargeFiles={field.displayLargeFiles}
                                                        showBackgroundImage={field.showBackgroundImage}
                                                        feedbackTest={holdingStore.feedback}
                                                        addNameAfterFieldname={field.addNameAfterFieldname}
                                                        showEmojis={field.showEmojis}
                                                    />
                                                </Localizer>

                                                {field.hideTextTemplate ? <></> : <>
                                                    {['textarea', 'textareaEditor'].includes(field.type) && object[field.name] && isInfocus[field.name] && <>
                                                        <div class='d-flex flex-row justify-content-end align-items-end'>
                                                            {isCustomerAdmin ? <>
                                                                <button
                                                                    class='btn btn-link btn-sm text-muted'
                                                                    onClick={this.saveTextTemplate}
                                                                    data-section={section.key}
                                                                    data-field={field.name}
                                                                >
                                                                    <i class='fa-duotone fa-floppy-disk' /> <Text id='input.save-text-template'>Save as template</Text>
                                                                </button>
                                                                {textTemplateSaved[field.name] && <>
                                                                    <div class='alert alert-success' role='alert'>
                                                                        <Text id='input.text-template-saved'>Text template saved</Text>
                                                                    </div>
                                                                </>}
                                                            </> : <></>}
                                                        </div>
                                                    </>}
                                                    {textTpls && textTpls.length > 0 && <>
                                                        <div class='mb-2 d-flex flex-row justify-content-end align-items-end'>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-primary rounded-pill px-3'
                                                                onClick={() => this.openTemplateModal(section.key, field.name)}
                                                            >
                                                                <i class='fa-duotone fa-text-size me-2' />
                                                                <Text id='input.choose-text-template'>Choose text template</Text>
                                                                <span class='badge bg-primary ms-2'>{textTpls.length}</span>
                                                            </button>
                                                        </div>
                                                    </>}
                                                </>}

                                                {field.displayInputInfo ? field.displayInputInfo(object) : <></>}

                                            </>}
                                        </div>
                                    </div>
                                </>);
                            })}
                        </div>
                        {section.isCollapsed && !sectionsNotCollapsed && <div class='d-flex flex-row justify-content-center px-3 mt-1'>
                            <button
                                class={`btn btn-link section-button text-muted`}
                                onClick={this.toggleSection}
                                data-key={section.key}
                            >
                                <i class='fa-duotone fa-chevron-up' /> <Text id='input.section-close'>Close section</Text>
                            </button>
                        </div>}

                        {!skipSurroundingDivs && section.help && <div class='mx-4 font-weight-lighter px-3 pb-1 text-muted' style='font-size: 1.0rem; line-height: 1.0rem;'>
                            <small class=''>
                                {section.help}
                            </small>
                        </div>}
                    </>}

                </>);
            })}

            {/* Text Template Modal */}
            {showTemplateModal && <>
                <div class='modal show d-block' tabindex='-1' style='background: rgba(0,0,0,0.5);'>
                    <div class='modal-dialog modal-lg modal-dialog-scrollable'>
                        <div class='modal-content'>
                            <div class='modal-header'>
                                <h5 class='modal-title'>
                                    <i class='fa-duotone fa-text-size me-2' />
                                    <Text id='input.choose-text-template'>Choose text template</Text>
                                </h5>
                                <button type='button' class='btn-close' onClick={this.closeTemplateModal}></button>
                            </div>
                            <div class='modal-body'>
                                <div class='row'>
                                    <div class='col-md-6'>
                                        <div class='mb-3'>
                                            <input
                                                type='text'
                                                class='form-control'
                                                placeholder={searchTemplatePlaceholder}
                                                value={templateSearchQuery}
                                                onInput={this.handleTemplateSearch}
                                            />
                                        </div>
                                        <div class='list-group' style='max-height: 400px; overflow-y: auto;'>
                                            {textTemplates
                                                .filter(t =>
                                                    t.section === templateModalSection &&
                                                    t.field === templateModalField &&
                                                    (templateSearchQuery === '' ||
                                                     (t.title && t.title.toLowerCase().includes(templateSearchQuery.toLowerCase())) ||
                                                     (t.body && t.body.toLowerCase().includes(templateSearchQuery.toLowerCase())))
                                                )
                                                .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'nb'))
                                                .map(t => (
                                                    <button
                                                        type='button'
                                                        class={`list-group-item list-group-item-action ${selectedTemplatePreview?.id === t.id ? 'active' : ''}`}
                                                        onClick={() => this.selectTemplateFromModal(t)}
                                                    >
                                                        <div class='d-flex flex-column w-100 justify-content-between align-items-start position-relative'>
                                                            <div>
                                                                <small>
                                                                    <small class='text-muted'>
                                                                        {t.title}
                                                                    </small>
                                                                </small>
                                                            </div>
                                                            <div class='w-100' style='line-height: 0.8rem;'>
                                                                <small class='font-style-narrow'>
                                                                    {t.body ? t.body.substring(0, 80) + (t.body.length > 80 ? '...' : '') : 'Empty template'}
                                                                </small>
                                                            </div>
                                                            <div class='d-flex flex-row justify-content-between align-items-center'>
                                                                <small>
                                                                    <small class='text-muted mt-1 me-2' style='white-space: nowrap;'>
                                                                        {util.formatDate(t.createdDate, { locale: 'nb-NO' })}
                                                                    </small>
                                                                </small>
                                                                {this.viewTags(t)}
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                    <div class='col-md-6'>
                                        <h6 class='mb-2'>
                                            <Text id='input.template-preview'>Preview</Text>
                                        </h6>
                                        {selectedTemplatePreview ? (
                                            <div class='card'>
                                                <div class='card-body'>
                                                    <div class='mb-2'>
                                                        <small class='text-muted'>
                                                            <i class='fa-duotone fa-calendar me-1' />
                                                            {util.formatDate(selectedTemplatePreview.createdDate, { locale: 'nb-NO' })}
                                                        </small>
                                                    </div>
                                                    <div class='card-text font-style-narrow' style='font-size: 0.9rem; line-height: 1.2rem;'>
                                                        <Markdown markdown={selectedTemplatePreview.body} markedOpts={MARKDOWN_OPTIONS} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div class='text-muted text-center py-5'>
                                                <i class='fa-duotone fa-file-lines fa-3x mb-3' />
                                                <p><Text id='input.select-template-to-preview'>Select a template to preview</Text></p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div class='modal-footer'>
                                <button type='button' class='btn btn-secondary rounded-pill' onClick={this.closeTemplateModal}>
                                    <Text id='input.cancel'>Cancel</Text>
                                </button>
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.applySelectedTemplate}
                                    disabled={!selectedTemplatePreview}
                                >
                                    <i class='fa-duotone fa-check me-2' />
                                    <Text id='input.use-template'>Use Template</Text>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </>);
    }
}

export default FormFields;
